import {
    SpanKind,
    SpanStatusCode,
    ROOT_CONTEXT,
    trace,
    type Span,
    type Attributes,
    type Link,
} from '@opentelemetry/api';
import { getTracer } from './setup-otel';

export type SpanLink = {
    traceId: string;
    spanId: string;
    attributes?: Attributes;
};

export type CreateSpanOptions = {
    /** Span name (e.g., 'ws.message.received', 'command-line.execute') */
    name: string;
    /** Optional span attributes */
    attributes?: Attributes;
    /** Optional explicit start time (epoch ms) */
    startTime?: number;
    /** Optional explicit end time (epoch ms). Only used by createSpan(). Defaults to Date.now(). */
    endTime?: number;
    /** Optional span links to connect to other traces */
    links?: SpanLink[];
    /** Optional span kind (defaults to INTERNAL) */
    kind?: SpanKind;
    /** Optional parent span — the new span will be created as a child */
    parentSpan?: Span;
};

function toOtelLinks(links: SpanLink[] | undefined): Link[] | undefined {
    if (!links || links.length === 0) {
        return undefined;
    }
    return links.map((link) => ({
        context: {
            traceId: link.traceId,
            spanId: link.spanId,
            traceFlags: 1, // sampled
        },
        attributes: link.attributes,
    }));
}

/**
 * Create and immediately end a span (fire-and-forget).
 * Use this for completed operations where timing is already known.
 *
 * Silently no-ops if OTel has not been initialized.
 *
 * @example
 * createSpan({
 *     name: 'ws.message.received',
 *     startTime: messageTimestamp,
 *     attributes: { messageType: 'UNIT_UPDATE', latency_ms: 42 },
 *     links: [{ traceId: backendTraceId, spanId: backendSpanId }],
 * });
 */
export function createSpan(options: CreateSpanOptions): void {
    const currentTracer = getTracer();
    if (!currentTracer) {
        return;
    }

    const ctx = options.parentSpan
        ? trace.setSpan(ROOT_CONTEXT, options.parentSpan)
        : undefined;

    const span = currentTracer.startSpan(
        options.name,
        {
            kind: options.kind ?? SpanKind.INTERNAL,
            attributes: { 'resource.name': options.name, ...options.attributes },
            startTime: options.startTime,
            links: toOtelLinks(options.links),
        },
        ctx,
    );

    span.setStatus({ code: SpanStatusCode.OK });
    span.end(options.endTime);
}

/**
 * Start a span and return it for manual ending.
 * Use this for operations with unknown duration.
 *
 * Returns a no-op span if OTel has not been initialized (safe to call .end() on it).
 *
 * @example
 * const span = startSpan({ name: 'command-line.execute' });
 * try {
 *     await executeCommand();
 * } finally {
 *     span.end();
 * }
 */
export function startSpan(options: CreateSpanOptions): Span {
    const currentTracer = getTracer();
    if (!currentTracer) {
        return noopSpan;
    }

    const ctx = options.parentSpan
        ? trace.setSpan(ROOT_CONTEXT, options.parentSpan)
        : undefined;

    return currentTracer.startSpan(
        options.name,
        {
            kind: options.kind ?? SpanKind.INTERNAL,
            attributes: { 'resource.name': options.name, ...options.attributes },
            startTime: options.startTime,
            links: toOtelLinks(options.links),
        },
        ctx,
    );
}

/** Minimal no-op span returned when OTel is not initialized */
const noopSpan: Span = {
    spanContext: () => ({
        traceId: '00000000000000000000000000000000',
        spanId: '0000000000000000',
        traceFlags: 0,
    }),
    setAttribute: () => noopSpan,
    setAttributes: () => noopSpan,
    addEvent: () => noopSpan,
    addLink: () => noopSpan,
    addLinks: () => noopSpan,
    setStatus: () => noopSpan,
    updateName: () => noopSpan,
    end: () => {},
    isRecording: () => false,
    recordException: () => {},
};
